import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { brand, nav, benefits, services, workTimes, faqs, images, prices as mockPrices, remoteSupport } from "../mock";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Card, CardContent } from "../components/ui/card";
import ChatWidget from "../components/ChatWidget";
import SeasonalDecorations from "../components/SeasonalDecorations";
import { useToast } from "../hooks/use-toast";
import { useSEO, SEO_PRESETS } from "../hooks/useSEO";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { CheckCircle, Shield, Clock, MapPin, Phone, Mail, Laptop2, Users, ArrowRight, Star, Zap, Gift, Heart, Sparkles } from "lucide-react";
import { postLead } from "../lib/api";

export const Section = ({ id, className = "", children }: { id: string; className?: string; children: React.ReactNode }) => (
  <section id={id} className={`relative py-16 md:py-24 overflow-hidden ${className}`}>
    <div className="mx-auto w-full max-w-7xl px-6">{children}</div>
  </section>
);

export const Header = () => {
  const scrollTo = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-white/80 backdrop-blur-xl supports-[backdrop-filter]:bg-white/60 shadow-sm">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        <div className="flex items-center gap-3">
          <div className="relative">
            <img 
              src="https://customer-assets.emergentagent.com/job_tech-support-mvp/artifacts/88uemucq_LogoKlartech%20%281%29.png" 
              alt="Klartech Müritz Logo" 
              className="h-12 w-auto object-contain transition-transform hover:scale-105"
            />
            <div className="absolute -right-1 -top-1 h-3 w-3 rounded-full bg-green-500 animate-pulse ring-2 ring-white"></div>
          </div>
        </div>
        <nav className="hidden items-center gap-8 md:flex">
          {nav.map((n) => (
            <button
              key={n.href}
              onClick={() => scrollTo(n.href)}
              className="relative text-sm font-medium text-slate-700 transition-all hover:text-primary group"
            >
              {n.label}
              <span className="absolute -bottom-1 left-0 h-0.5 w-0 bg-primary transition-all group-hover:w-full"></span>
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/gutschein">
            <Button
              variant="outline"
              className="hidden sm:flex items-center gap-2 rounded-xl border-2 border-red-200 bg-red-50 px-4 py-2 text-red-700 font-semibold hover:bg-red-100 hover:border-red-300 transition-all"
            >
              <Gift className="h-4 w-4" />
              Gutschein
            </Button>
          </Link>
          <Button
            onClick={() => scrollTo("#kontakt")}
            className="relative rounded-xl bg-gradient-to-r from-primary to-blue-600 px-6 py-2.5 text-white shadow-lg transition-all hover:shadow-xl hover:scale-105 group overflow-hidden"
          >
            <span className="relative z-10 font-semibold">Kontakt</span>
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-primary opacity-0 transition-opacity group-hover:opacity-100"></div>
          </Button>
        </div>
      </div>
    </header>
  );
};

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <Section id="home" className="pt-8 md:pt-12 bg-gradient-to-br from-slate-50 via-blue-50/30 to-white relative">
      <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-br from-blue-100/40 to-indigo-100/40 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-gradient-to-br from-cyan-200/20 to-blue-200/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
      
      <div className="grid items-center gap-16 lg:grid-cols-12 relative z-10">
        <div className={`lg:col-span-7 transition-all duration-1000 ${isVisible ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-green-100 to-blue-100 px-4 py-2 text-sm font-medium text-slate-700 mb-6 shadow-sm">
            <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse"></div>
            Lokaler IT-Service • Verfügbar • Vertrauenswürdig
          </div>
          
          <h1 className="text-balance text-4xl font-black tracking-tight text-slate-900 sm:text-5xl lg:text-6xl leading-tight">
            Technik-Hilfe –{" "}
            <span className="bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">
              Klar & verständlich
            </span>
          </h1>
          
          <p className="mt-6 text-xl text-slate-600 leading-relaxed max-w-2xl">
            Ihr lokaler IT-Helfer in {brand.contact.location} für Senioren, Privatleute und Kleinunternehmen. 
            <strong className="text-slate-700"> Schluss mit Technik-Kaudawelsch</strong> – Sie bekommen Hilfe, die wirklich ankommt.
          </p>
          
          <div className="mt-8 flex flex-col sm:flex-row items-start gap-4">
            <Button
              onClick={() => window.location.href = '/termine'}
              className="group relative rounded-xl bg-gradient-to-r from-primary to-blue-600 px-8 py-4 text-lg font-bold text-white shadow-xl transition-all hover:shadow-2xl hover:scale-105 overflow-hidden"
            >
              <span className="relative z-10 flex items-center gap-2">
                📅 Jetzt Termin buchen
                <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
              </span>
            </Button>
            
            <Button
              variant="outline"
              onClick={() => document.querySelector("#kontakt")?.scrollIntoView({ behavior: "smooth" })}
              className="rounded-xl border-2 border-primary bg-white px-8 py-4 text-lg font-semibold text-primary transition-all hover:bg-primary hover:text-white hover:shadow-lg"
            >
              Kostenloses Erstgespräch
            </Button>
          </div>
          
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="flex items-center gap-3 p-4 rounded-xl bg-white/60 backdrop-blur-sm shadow-sm border border-white/20">
              <div className="p-2 rounded-full bg-green-100">
                <Shield size={20} className="text-green-600" />
              </div>
              <div>
                <div className="font-semibold text-slate-800">Sicher & transparent</div>
                <div className="text-sm text-slate-600">Vertrauensvolle Beratung</div>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-4 rounded-xl bg-white/60 backdrop-blur-sm shadow-sm border border-white/20">
              <div className="p-2 rounded-full bg-blue-100">
                <Zap size={20} className="text-blue-600" />
              </div>
              <div>
                <div className="font-semibold text-slate-800">Schnell verfügbar</div>
                <div className="text-sm text-slate-600">Zeitnahe Hilfe</div>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-4 rounded-xl bg-white/60 backdrop-blur-sm shadow-sm border border-white/20">
              <div className="p-2 rounded-full bg-purple-100">
                <MapPin size={20} className="text-purple-600" />
              </div>
              <div>
                <div className="font-semibold text-slate-800">Lokal in {brand.contact.location}</div>
                <div className="text-sm text-slate-600">Vor Ort & Remote</div>
              </div>
            </div>
          </div>
        </div>
        
        <div className={`lg:col-span-5 relative transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 transform translate-y-0' : 'opacity-0 transform translate-y-8'}`}>
          <Card className="overflow-hidden rounded-3xl border-0 shadow-2xl ring-1 ring-slate-200/60 transform hover:scale-105 transition-transform duration-500">
            <CardContent className="p-0 relative">
              <img 
                src={images.hero} 
                alt="Senioren IT Hilfe" 
                className="h-96 w-full object-cover lg:h-[500px]" 
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </Section>
  );
};

const Why = () => {
  const icons = [CheckCircle, Clock, Shield, MapPin, Users, Laptop2];
  
  return (
    <Section id="warum" className="bg-gradient-to-b from-white to-slate-50 relative">
      <div className="text-center mb-16">
        <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">
          Warum <span className="bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">{brand.name}</span>?
        </h2>
      </div>
      
      <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
        {benefits.map((b, i) => {
          const Icon = icons[i % icons.length];
          return (
            <div key={b.title} className="group relative rounded-3xl border border-slate-200 bg-white p-8 shadow-sm transition-all duration-300 hover:shadow-xl hover:scale-105">
              <div className="absolute -top-6 left-8">
                <div className="inline-flex rounded-2xl bg-gradient-to-br from-primary to-blue-600 p-4 shadow-lg">
                  <Icon size={24} className="text-white" />
                </div>
              </div>
              <div className="pt-6">
                <h3 className="text-lg font-bold text-slate-900 mb-3">{b.title}</h3>
                <p className="text-slate-600 leading-relaxed">{b.desc}</p>
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
};

const Services = () => {
  const Item = ({ text }: { text: string }) => (
    <li className="flex items-start gap-3 text-slate-700 group">
      <CheckCircle size={20} className="mt-0.5 shrink-0 text-primary transition-transform group-hover:scale-110" />
      <span className="leading-relaxed">{text}</span>
    </li>
  );

  return (
    <Section id="leistungen" className="bg-white relative">
      <div className="text-center mb-16">
        <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">
          Unsere <span className="bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">Leistungen</span>
        </h2>
      </div>

      <div className="grid items-start gap-16 lg:grid-cols-2">
        <div className="group">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl p-8">
            <h3 className="text-2xl font-bold text-slate-900 mb-3">{services.business.title}</h3>
            <ul className="space-y-4 mb-8">
              {services.business.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700 group">
                  <CheckCircle size={20} className="mt-0.5 shrink-0 text-primary transition-transform group-hover:scale-110" />
                  <span className="leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        
        <div className="group">
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl p-8">
            <h3 className="text-2xl font-bold text-slate-900 mb-3">{services.seniors.title}</h3>
            <ul className="space-y-4 mb-8">
              {services.seniors.bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-700 group">
                  <CheckCircle size={20} className="mt-0.5 shrink-0 text-primary transition-transform group-hover:scale-110" />
                  <span className="leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </Section>
  );
};

const Times = () => {
  return (
    <Section id="zeiten" className="bg-white relative">
      <div className="text-center mb-16">
        <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">
          <span className="bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">Öffnungszeiten</span>
        </h2>
      </div>
      
      <div className="max-w-md mx-auto bg-slate-50 rounded-2xl p-8">
        {workTimes.map((wt) => (
          <div key={wt.day} className="flex justify-between py-3 border-b border-slate-200 last:border-0">
            <span className="font-medium text-slate-700">{wt.day}</span>
            <span className="text-slate-600">{wt.time}</span>
          </div>
        ))}
      </div>
    </Section>
  );
};

const FAQ = () => {
  return (
    <Section id="faq" className="bg-slate-50">
      <div className="text-center mb-16">
        <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">
          Häufige <span className="bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">Fragen</span>
        </h2>
      </div>
      
      <div className="max-w-3xl mx-auto">
        <Accordion type="single" collapsible className="space-y-4">
          {faqs.map((faq, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="bg-white rounded-xl border-0 shadow-sm px-6">
              <AccordionTrigger className="text-left font-semibold text-slate-800 hover:no-underline">
                {faq.q}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed">
                {faq.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </Section>
  );
};

// Gift Voucher Promotional Banner
const GiftVoucherBanner = () => (
  <Section id="gutschein-banner" className="bg-gradient-to-br from-red-50 via-pink-50 to-orange-50 relative overflow-hidden">
    {/* Decorative elements */}
    <div className="absolute top-10 left-10 w-32 h-32 bg-red-200/30 rounded-full blur-2xl"></div>
    <div className="absolute bottom-10 right-10 w-40 h-40 bg-pink-200/30 rounded-full blur-2xl"></div>
    
    <div className="relative z-10 max-w-4xl mx-auto text-center">
      <div className="inline-flex items-center gap-2 rounded-full bg-red-100 px-5 py-2 text-red-700 font-semibold mb-6">
        <Sparkles className="h-4 w-4" />
        Geschenkidee
      </div>
      
      <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-6">
        <span className="bg-gradient-to-r from-red-600 to-pink-600 bg-clip-text text-transparent">IT-Hilfe verschenken</span>
      </h2>
      
      <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-8 leading-relaxed">
        Das perfekte Geschenk für Eltern oder Großeltern! Professionelle Unterstützung bei Computer, 
        Smartphone oder Tablet – <strong className="text-slate-800">geduldig erklärt, ohne Fachsprache</strong>.
      </p>
      
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
        <div className="flex items-center gap-2 text-slate-700">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <span>Flexibler Betrag (10€ – 500€)</span>
        </div>
        <div className="flex items-center gap-2 text-slate-700">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <span>Sofort per E-Mail</span>
        </div>
        <div className="flex items-center gap-2 text-slate-700">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <span>12 Monate gültig</span>
        </div>
      </div>
      
      <Link to="/gutschein">
        <Button className="rounded-xl bg-gradient-to-r from-red-600 to-pink-600 px-8 py-6 text-lg font-bold text-white shadow-xl hover:shadow-2xl hover:scale-105 transition-all">
          <Gift className="mr-2 h-5 w-5" />
          Gutschein kaufen
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
      </Link>
      
      <p className="mt-6 text-sm text-slate-500 flex items-center justify-center gap-2">
        <Heart className="h-4 w-4 text-red-400" />
        Über 100 verschenkte Gutscheine – Freude garantiert!
      </p>
    </div>
  </Section>
);

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", newsletter: false });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await postLead(form);
      toast({ title: "Nachricht gesendet!", description: "Wir melden uns bald bei Ihnen." });
      setForm({ name: "", email: "", phone: "", message: "", newsletter: false });
    } catch {
      toast({ title: "Fehler", description: "Bitte versuchen Sie es später erneut.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section id="kontakt" className="bg-white">
      <div className="text-center mb-16">
        <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4">
          <span className="bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent">Kontakt</span>
        </h2>
      </div>
      
      <div className="grid gap-12 lg:grid-cols-2">
        <div>
          <h3 className="text-xl font-bold text-slate-900 mb-6">Kontaktdaten</h3>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Phone size={20} className="text-primary" />
              <span>{brand.contact.phone}</span>
            </div>
            <div className="flex items-center gap-3">
              <Mail size={20} className="text-primary" />
              <span>{brand.contact.email}</span>
            </div>
            <div className="flex items-center gap-3">
              <MapPin size={20} className="text-primary" />
              <span>{brand.contact.location}</span>
            </div>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input placeholder="Name *" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required />
          <Input type="email" placeholder="E-Mail *" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} required />
          <Input placeholder="Telefon" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} />
          <Textarea placeholder="Ihre Nachricht *" value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} required rows={4} />
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? "Wird gesendet..." : "Nachricht senden"}
          </Button>
        </form>
      </div>
    </Section>
  );
};

export const Footer = () => (
  <footer className="bg-slate-900 text-white py-12">
    <div className="max-w-7xl mx-auto px-6">
      <div className="flex flex-col md:flex-row justify-between items-center gap-6">
        <div className="text-slate-400 text-sm">
          © {new Date().getFullYear()} {brand.name}. Alle Rechte vorbehalten.
        </div>
        <div className="flex gap-6 text-sm">
          <a href="/impressum" className="text-slate-400 hover:text-white transition-colors">Impressum</a>
          <a href="/datenschutz" className="text-slate-400 hover:text-white transition-colors">Datenschutz</a>
        </div>
      </div>
    </div>
  </footer>
);

export default function Landing() {
  // SEO für Hauptseite
  useSEO(SEO_PRESETS.home);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <SeasonalDecorations />
      <Header />
      <Hero />
      <Why />
      <Services />
      <Times />
      <FAQ />
      <GiftVoucherBanner />
      <Contact />
      <Footer />
      <ChatWidget />
    </div>
  );
}
